import { inject } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore } from 'bernie-core';

import { NavigationBarTabModel } from 'models';

import { UitkNavigationType, UitkTab, UitkTabs, UitkTabsType } from 'uitk-react-tabs';
import { UitkSticky } from 'uitk-react-sticky';

import { NavigationRefs } from './navigation-refs';
import * as viewportListener from 'components/common/lazy/viewport-listener';

interface NavigationTabs {
  [key: string]: boolean;
}

interface NavigationBarProps {
  analytics?: AnalyticsStore;
  navigationBarTabList: NavigationBarTabModel[];
}

interface NavigationBarState {
  jsEnabled: boolean;
  selectedJumplinkTab: string;
  suspendActiveTabTracking: boolean;
}

const ACTIVE_TAB_THRESHOLD: number = 60;
const SCROLL_TIMEOUT: number = 2000;

@inject('analytics')
export class NavigationBar extends React.Component<NavigationBarProps, NavigationBarState> {
  private tabs: NavigationTabs = {};
  private trackingTimeout: number;

  constructor(props) {
    super(props);

    this.state = {
      selectedJumplinkTab: '',
      jsEnabled: false,
      suspendActiveTabTracking: false
    };

    this.setJumplinkTabSelect = this.setJumplinkTabSelect.bind(this);
    this.handleTabClickedTracker = this.handleTabClickedTracker.bind(this);
  }

  public componentDidMount() {
    this.setState({ jsEnabled: true });

    setTimeout(() => {
      const { navigationBarTabList } = this.props;

      navigationBarTabList.map(({ tabSection, localizedTabName }) => {
        this.tabs[localizedTabName] = false;

        viewportListener.subscribe(
          'window',
          NavigationRefs[tabSection].current,
          {
            isAbove: () => this.handleViewportEvent(localizedTabName, true),
            isAboveTop: () => this.handleViewportEvent(localizedTabName, true),
            isBelowTop: () => this.handleViewportEvent(localizedTabName, false),
            isBelow: () => this.handleViewportEvent(localizedTabName, false)
          },
          {
            offset: { top: ACTIVE_TAB_THRESHOLD, bottom: 0 }
          }
        );
      });

      this.handleViewportEvent();
    }, 0);
  }

  public componentWillUnmount() {
    const { navigationBarTabList } = this.props;

    navigationBarTabList.map(({ tabSection }) => {
      viewportListener.unsubscribe('window', NavigationRefs[tabSection].current);
    });
  }

  private handleViewportEvent(tab: string = null, active: boolean = false) {
    const tabKeys = Object.keys(this.tabs);
    const defaultTabKey = tabKeys[0];

    if (tabKeys.includes(tab)) {
      this.tabs[tab] = active;
    }

    if (this.state.suspendActiveTabTracking) {
      return;
    }

    const activeTab = tabKeys.reverse().find((key: string) => this.tabs[key]);

    this.setJumplinkTabSelect(!!activeTab ? activeTab : defaultTabKey, true);
  }

  public setJumplinkTabSelect(selectedJumplinkTab, isScrollEvent = false) {
    if (!selectedJumplinkTab) {
      this.setState({ selectedJumplinkTab: null });

      return;
    }

    const { navigationBarTabList } = this.props;
    const selectedTab: NavigationBarTabModel = navigationBarTabList.find(
      navigationBarTab => navigationBarTab.localizedTabName === selectedJumplinkTab
    );

    if (selectedTab) {
      if (!isScrollEvent) {
        this.handleTabClickedTracker(selectedTab);

        clearTimeout(this.trackingTimeout);
        this.trackingTimeout = window.setTimeout(() => this.setState({ suspendActiveTabTracking: false }), SCROLL_TIMEOUT);
      }

      this.setState({
        selectedJumplinkTab,
        suspendActiveTabTracking: !isScrollEvent
      });
    }
  }

  public handleTabClickedTracker(selectedTab) {
    const { analytics } = this.props;
    if (analytics && analytics.clickTracker) {
      const tabSection = selectedTab.tabSection;
      analytics.clickTracker(`.NavBar.${tabSection}`, tabSection, null, null, null)();
    }
  }

  public render() {
    const { navigationBarTabList } = this.props;
    const { jsEnabled, selectedJumplinkTab } = this.state;

    return (
      <AbacusExperiment name="Lshop_PDP_NavBar">
        <AbacusControl>
          <StickyNavBar
            jsEnabled={jsEnabled}
            navigationBarTabList={navigationBarTabList}
            selectedJumplinkTab={selectedJumplinkTab}
            setJumplinkTabSelect={this.setJumplinkTabSelect}
          />
        </AbacusControl>
        <AbacusVariant bucket={1}>
          <StickyNavBar
            jsEnabled={jsEnabled}
            navigationBarTabList={navigationBarTabList}
            selectedJumplinkTab={selectedJumplinkTab}
            setJumplinkTabSelect={this.setJumplinkTabSelect}
            displayOverviewTab={true}
          />
        </AbacusVariant>
      </AbacusExperiment>
    );
  }
}

interface StickyNavBarProps {
  jsEnabled: boolean;
  navigationBarTabList: NavigationBarTabModel[];
  selectedJumplinkTab: string;
  setJumplinkTabSelect: (e: any) => void;
  displayOverviewTab?: boolean;
}

const StickyNavBar: React.FunctionComponent<StickyNavBarProps> = ({
  jsEnabled,
  navigationBarTabList,
  selectedJumplinkTab,
  setJumplinkTabSelect,
  displayOverviewTab
}) => (
  <>
    {jsEnabled && (
      <UitkSticky zIndex={300}>
        <UitkTabs
          navigationType={UitkNavigationType.JUMPLINK}
          onTabSelect={setJumplinkTabSelect}
          selectedTab={selectedJumplinkTab}
          tabsType={UitkTabsType.NATURAL}
          scrollOffsetSelf={true}
          data-stid="property-details-page-navigation-bar"
        >
          {navigationBarTabList
            .filter(({ tabSection }) =>
              tabSection !== 'Overview' || displayOverviewTab)
            .map(({ tabSection, localizedTabName }) => (
              <UitkTab key={`uitk-tab-key-${tabSection}`} name={localizedTabName} targetRef={NavigationRefs[tabSection]} />
            ))
          }
        </UitkTabs>
      </UitkSticky>
    )}
  </>
);

StickyNavBar.displayName = 'StickyNavBar';
